import { customElement } from 'lit/decorators.js';
import { EventGetReference, EventSnippet, EventTemplate } from '../../../webmodule-common/other/ui/events';
import {
  FabricationJobState,
  FabricationJobStateCount,
  FabricationJobSummaryView
} from '../../api/fabrication-job-api';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import {
  isOptimisticLockOwner,
  optimisticLock,
  optimisticLockRelease
} from '../../dealer-franchisee/common/optimistic-lock';
import { FabricationJobSummaryTable } from './fabrication-job-summary-table';
import { getApiFactory } from '../../api/api-injector';
import { html } from 'lit';
import { LangFabricationJobListView } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-list-view-lang';
import { IconRefresh } from '../../../webmodule-common/other/ui/icons/icon-refresh';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';

export interface FabricationJobListViewOptions {
  branchId: EventGetReference;
}

@customElement('wm-fabricationjoblistview')
export class FabricationJobListView extends ViewBase {
  protected readonly branchId: EventGetReference;
  protected tabManager: TabManager;
  private readonly elementId: string;
  protected tables: FabricationJobSummaryTable[];
  protected stateCounts: FabricationJobStateCount[] = [];
  protected staleJobs: string[] = [];

  constructor(options: FabricationJobListViewOptions) {
    super();
    this.elementId = `fabrication-job-list-view-${newGuid()}`;
    this.branchId = options.branchId;
    this.tables = this.initializeTables();
    this.tabManager = this.createTabManager();
  }

  public async setActiveTabByHash() {
    await this.tabManager?.applyWindowHash();
  }

  public async afterConstruction(): Promise<void> {
    await this.updatePageTotals();
    const table = this.tabManager.activePage?.data as FabricationJobSummaryTable;
    if (table) await table.refreshData(1);
  }

  protected createTabManager(): TabManager {
    const getInitialPages = (): TabPage[] => {
      return this.tables.map(table => {
        const page: TabPage = {
          caption: table.eventTitle,
          onEnter: () => {
            // enable data load the first time we enter the tab, but not before
            table.delayedDataLoad = false;
            // not awaited on purpose, for a faster tab switch; safe to do
            if (table.dataIsStale) table.refreshData();
          },
          content: () => table,
          data: table,
          fragment: table.pageFragment
        };
        return page;
      });
    };

    return new TabManager({
      initialIndex: 0,
      actions: [
        {
          caption: () => html`${new IconRefresh()}`,
          onClick: async () => {
            const table = this.tabManager.activePage?.data as FabricationJobSummaryTable | undefined;
            if (!table) return;
            this.beforeRefresh();
            await table.refreshData();
            this.tables.forEach(t => {
              if (t !== table) t.dataIsStale = true;
            });
            this.afterRefresh();
          }
        }
      ],
      addAction: {
        caption: () => LangFabricationJobListView.createFabricationJob(),
        onClick: async () => {
          await this.addNewFabricationJob();
        }
      },
      pages: () => getInitialPages()
    });
  }

  beforeRefresh() {
    this.staleJobs = [];
  }

  afterRefresh() {
    // overridden in subclass
  }

  protected async updatePageTotals() {
    const api = getApiFactory().fabricationJob();
    const result = await api.getFabricationJobTotalsByState({ branchId: await this.branchId() });
    if (result) this.stateCounts = result.stateCounts;
    this.tabManager.requestUpdate();
  }

  protected getCount(...states: FabricationJobState[]): number {
    return this.stateCounts.filter(sc => states.includes(sc.state)).reduce((sum, sc) => sum + sc.count, 0);
  }

  protected initializeTables(): FabricationJobSummaryTable[] {
    const makeTable = (titleStr: () => string, states: FabricationJobState[], pageFragment: string) => {
      const title: EventSnippet = () => {
        const count = this.getCount(...states);
        return html`${titleStr()} (${count})`;
      };
      return new FabricationJobSummaryTable({
        states,
        title,
        branchId: this.branchId,
        pageFragment,
        deleteJobEvent: async job => await this.deleteJob(job),
        stale: { exists: (id: string) => this.staleJobs.includes(id) }
      });
    };

    return [
      makeTable(
        () => LangFabricationJobListView.allCurrentTabTitle(),
        [
          FabricationJobState.Draft,
          FabricationJobState.Planned,
          FabricationJobState.AwaitingOrder,
          FabricationJobState.ReadyForProduction,
          FabricationJobState.InProduction
        ],
        'allCurrent'
      ),
      makeTable(() => LangFabricationJobListView.draftTabTitle(), [FabricationJobState.Draft], 'draft'),
      makeTable(() => LangFabricationJobListView.plannedTabTitle(), [FabricationJobState.Planned], 'planned'),
      makeTable(
        () => LangFabricationJobListView.awaitingOrderTabTitle(),
        [FabricationJobState.AwaitingOrder],
        'awaitingOrder'
      ),
      makeTable(
        () => LangFabricationJobListView.readyForProductionTabTitle(),
        [FabricationJobState.ReadyForProduction],
        'readyForProduction'
      ),
      makeTable(
        () => LangFabricationJobListView.inProductionTabTitle(),
        [FabricationJobState.InProduction],
        'inProduction'
      ),
      makeTable(() => LangFabricationJobListView.completedTabTitle(), [FabricationJobState.Completed], 'completed')
    ];
  }

  protected async deleteJob(job: FabricationJobSummaryView): Promise<void> {
    // Acquire a lock to ensure no other user is currently editing this job.
    // NOTE: The same lock-before-delete pattern may be missing from quote list deletion.
    const lock = await optimisticLock('fabricationjob', job.id);
    if (!isOptimisticLockOwner(lock)) {
      await information(
        LangFabricationJobListView.cannotDeleteMessage(),
        LangFabricationJobListView.cannotDeleteTitle()
      );
      return;
    }

    try {
      const api = getApiFactory().fabricationJob();
      // Fetch the latest record version — the summary list data can be stale
      const latest = await api.getFabricationJob({ id: job.id });
      if (!latest) return;
      const result = await api.deleteFabricationJob({
        id: job.id,
        recordVersion: latest.fabricationJob.recordVersion
      });
      if (result) {
        await this.updatePageTotals();
        const table = this.tabManager.activePage?.data as FabricationJobSummaryTable;
        if (table) await table.refreshData();
      }
    } finally {
      await optimisticLockRelease(job.id);
    }
  }

  async addNewFabricationJob(): Promise<boolean> {
    const branchId = await this.branchId();
    const id = newGuid();
    const result = await getApiFactory().fabricationJob().createFabricationJob({
      id,
      branchId,
      title: '',
      description: ''
    });
    if (result) {
      window.location.hash = '';
      window.location.pathname = `/fabrication-jobs/${result.fabricationJob.id}`;
      return true;
    }
    return false;
  }

  protected template(): EventTemplate {
    return html`<div id=${this.elementId} class="page-content">${this.tabManager?.ui}</div>`;
  }
}
